export interface PreparedBatchCredentials {
  accounts: string;
  passwords?: string;
  emails: string[];
}

export type BatchCredentialProvider = "auto" | "google" | "microsoft";

interface ParsedBatchAccount {
  email: string;
  password: string;
  /** A valid Higgsfield Cookie row does not require a password field. */
  cookieFirst?: boolean;
}

type GoogleCredentialFields = [email: string, password: string, recoveryEmail: string];

const OAUTH_CLIENT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const BATCH_EMAIL_PATTERN = /^[^\s@|,]+@[^\s@|,]+\.[^\s@|,]+$/;

export function prepareBatchCredentials(
  accountsText: string,
  passwordsText: string,
  provider: BatchCredentialProvider = "auto",
): PreparedBatchCredentials {
  if (provider === "google") return prepareExplicitGoogleCredentials(accountsText, passwordsText);
  if (provider === "microsoft") rejectGoogleRowsInMicrosoftMode(accountsText);
  const emails = parseBatchAccounts(accountsText, passwordsText);
  return {
    // These values are intentionally opaque. The backend owns credential
    // normalization, including escaped characters in refresh credentials.
    accounts: accountsText,
    ...(passwordsText.trim() ? { passwords: passwordsText } : {}),
    emails,
  };
}

export function summarizeBatchInput(value: string, provider: BatchCredentialProvider = "auto"): string {
  const rows = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!rows.length) return "";
  if (provider === "google") {
    const cookieRows = rows.filter((line) => googleCookieCredentialEmail(line) !== null).length;
    return cookieRows
      ? `已读取 ${rows.length} 行 Google 凭据，其中 ${cookieRows} 行带 Higgsfield Cookie；提交时优先恢复 Cookie 会话`
      : `已读取 ${rows.length} 行 Google 凭据；提交时会逐行添加 Google 登录标记`;
  }
  if (provider === "microsoft") {
    return `已读取 ${rows.length} 行 Microsoft 凭据；提交时使用 Microsoft 登录流程`;
  }
  const tabRows = rows.filter((line) => line.includes("\t")).length;
  const cookieRows = rows.filter((line) => googleCookieCredentialEmail(line) !== null).length;
  const googleRows = rows.filter((line) => googleCredentialFields(line) !== null).length;
  const compactPipeRows = rows.filter((line) => compactPipeCredentialFields(line) !== null).length;
  const embeddedClientRows = rows.filter((line) => {
    const fields = line.split("\t").map((field) => field.trim()).filter(Boolean);
    const bundleParts = (fields[2] || "").split("|");
    return bundleParts.length === 4 && OAUTH_CLIENT_ID_PATTERN.test(bundleParts[3]);
  }).length;
  const detail = cookieRows
    ? `已读取 ${rows.length} 行，检测到 ${cookieRows} 行带 Higgsfield Cookie`
    : googleRows
    ? `已读取 ${rows.length} 行，检测到 ${googleRows} 行 Google 恢复邮箱凭据`
    : compactPipeRows
    ? `已读取 ${rows.length} 行，检测到 ${compactPipeRows} 行四段 Pipe 凭据`
    : tabRows
    ? embeddedClientRows
      ? `已读取 ${rows.length} 行，检测到 ${embeddedClientRows} 行含内嵌 OAuth Client ID`
      : `已读取 ${rows.length} 行 Tab 凭据`
    : `已读取 ${rows.length} 行凭据`;
  return `${detail}；提交时自动逐行识别`;
}

function prepareExplicitGoogleCredentials(accountsText: string, passwordsText: string): PreparedBatchCredentials {
  if (passwordsText.trim()) throw new Error("Google 批量登录请将凭据写在账号行中，Cookie 行无需另填密码");
  const rows = accountsText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!rows.length) throw new Error("至少输入一个账号");
  if (rows.length > 500) throw new Error("单次最多导入 500 个账号");

  const parsed = rows.map((row, index) => {
    const cookieEmail = googleCookieCredentialEmail(row);
    if (cookieEmail !== null) {
      return { email: cookieEmail, password: "", canonical: row };
    }
    const value = row.slice(0, 7).toLowerCase() === "google:" ? row.slice(7) : row;
    const separator = value.indexOf("|");
    if (separator <= 0) throw new Error(`第 ${index + 1} 行 Google 凭据应为 email|password`);
    const email = value.slice(0, separator).trim();
    const password = value.slice(separator + 1).trim();
    if (!BATCH_EMAIL_PATTERN.test(email)) throw new Error(`第 ${index + 1} 行邮箱格式不正确`);
    if (!password || password.length > 1024 || /[\x00-\x1f\x7f]/.test(password)) {
      throw new Error(`第 ${index + 1} 行 Google 密码格式不正确`);
    }
    return {
      email,
      password,
      canonical: `google:${email}|${password}`,
    };
  });
  const emails = parsed.map(({ email }) => email.toLowerCase());
  if (new Set(emails).size !== emails.length) throw new Error("批量账号中存在重复邮箱");

  return {
    // The explicit prefix is consumed by the backend batch parser and keeps
    // two-column Google rows out of the legacy Microsoft OAuth path. Cookie
    // export rows remain opaque so the backend can resume their session.
    accounts: parsed.map(({ canonical }) => canonical).join("\n"),
    emails,
  };
}

function rejectGoogleRowsInMicrosoftMode(accountsText: string): void {
  const rows = accountsText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const googleRow = rows.findIndex((line) => /^google:/i.test(line) || googleCredentialFields(line) !== null || googleCookieCredentialEmail(line) !== null);
  if (googleRow >= 0) throw new Error(`第 ${googleRow + 1} 行是 Google 凭据，请将登录提供商切换为 Google`);
}

/** Detect the six-column Google export carrying a Higgsfield Cookie header. */
function googleCookieCredentialEmail(line: string): string | null {
  if ((line.match(/----/g) || []).length < 5) return null;
  const fields = line.split("----");
  if (fields.length < 6) return null;
  const email = fields[0].trim();
  if (!BATCH_EMAIL_PATTERN.test(email)) return null;
  const tail = fields.slice(5).join("----");
  return tail.split("\t").some((part) => /(?:^|\s)__client\s*=/i.test(part.trim()))
    ? email
    : null;
}

export function stripInlineBatchSecrets(value: string): string {
  return value.split(/\r?\n/).map((line) => {
    const trimmed = line.trim();
    if (!trimmed) return "";
    const separatorIndexes = [
      trimmed.indexOf("\t"),
      trimmed.indexOf("---"),
      trimmed.indexOf("|"),
      trimmed.indexOf(","),
      trimmed.search(/\s{2,}/),
    ].filter((index) => index > 0);
    const end = separatorIndexes.length ? Math.min(...separatorIndexes) : trimmed.length;
    const email = trimmed.slice(0, end).trim();
    return BATCH_EMAIL_PATTERN.test(email) ? email.toLowerCase() : "";
  }).filter(Boolean).join("\n");
}

function parseBatchAccounts(accountsText: string, passwordsText: string): string[] {
  const lines = accountsText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) throw new Error("至少输入一个账号");
  if (lines.length > 500) throw new Error("单次最多导入 500 个账号");
  const parsed = lines.map((line, index) => {
    const parts = splitBatchAccountLine(line, index + 1);
    if (!BATCH_EMAIL_PATTERN.test(parts.email)) throw new Error(`第 ${index + 1} 行邮箱格式不正确`);
    return parts;
  });
  const emails = parsed.map((item) => item.email.toLowerCase());
  if (new Set(emails).size !== emails.length) throw new Error("批量账号中存在重复邮箱");

  const trimmedPasswords = passwordsText.trim();
  const passwordLines = trimmedPasswords ? trimmedPasswords.split(/\r?\n/).map((line) => line.trim()) : [];
  const missingIndexes = parsed.map((item, index) => (
    item.password || item.cookieFirst ? -1 : index
  )).filter((index) => index >= 0);
  if (passwordLines.length && !missingIndexes.length) throw new Error("账号行已包含密码，请清空独立密码输入");
  if (passwordLines.length > 1 && passwordLines.length !== parsed.length && passwordLines.length !== missingIndexes.length) throw new Error("逐行密码数量必须与账号总数或缺少密码的账号数一致");
  if (passwordLines.length === parsed.length) {
    const ambiguous = parsed.findIndex((item, index) => Boolean(item.password && passwordLines[index]));
    if (ambiguous >= 0) throw new Error(`第 ${ambiguous + 1} 个账号存在重复密码来源`);
  }
  const sharedPassword = passwordLines.length === 1 ? passwordLines[0] : "";
  const missingPasswords = passwordLines.length === missingIndexes.length && passwordLines.length !== parsed.length
    ? new Map(missingIndexes.map((index, position) => [index, passwordLines[position]]))
    : null;
  const missingPassword = parsed.findIndex((item, index) => (
    !item.password
    && !item.cookieFirst
    && !(sharedPassword || missingPasswords?.get(index) || passwordLines[index])
  ));
  if (missingPassword >= 0) throw new Error(`第 ${missingPassword + 1} 个账号缺少密码`);
  return emails;
}

function splitBatchAccountLine(line: string, rowNumber: number): ParsedBatchAccount {
  const cookieEmail = googleCookieCredentialEmail(line);
  if (cookieEmail !== null) {
    // Keep the complete row opaque for the backend; this marker only tells
    // the local preview that a password is intentionally omitted.
    return { email: cookieEmail, password: "", cookieFirst: true };
  }
  if (line.includes("\t")) return splitTabBatchAccountLine(line, rowNumber);
  const googleCredential = splitGoogleBatchAccountLine(line, rowNumber);
  if (googleCredential) return googleCredential;
  const compactPipe = splitCompactPipeBatchAccountLine(line, rowNumber);
  if (compactPipe) return compactPipe;
  const whitespaceFields = line.trim().split(/\s{2,}/);
  if (
    (whitespaceFields.length === 4
      && (whitespaceFields[2].match(/\|/g) || []).length === 2
      && OAUTH_CLIENT_ID_PATTERN.test(whitespaceFields[3]))
    || (whitespaceFields.length === 3
      && (whitespaceFields[2].match(/\|/g) || []).length === 3
      && OAUTH_CLIENT_ID_PATTERN.test(whitespaceFields[2].split("|")[3]))
  ) {
    return splitTabBatchAccountLine(whitespaceFields.join("\t"), rowNumber);
  }
  const separators = ["---", "|", ","]
    .map((separator) => ({ separator, index: line.indexOf(separator) }))
    .filter((item) => item.index > 0)
    .sort((left, right) => left.index - right.index);
  if (!separators.length) return { email: line.trim(), password: "" };
  const match = separators[0];
  return {
    email: line.slice(0, match.index).trim(),
    password: line.slice(match.index + match.separator.length).trim(),
  };
}

function googleCredentialFields(line: string): GoogleCredentialFields | null {
  const firstSeparator = line.indexOf("---");
  const lastSeparator = line.lastIndexOf("---");
  if (firstSeparator <= 0 || lastSeparator <= firstSeparator) return null;

  const fields: GoogleCredentialFields = [
    line.slice(0, firstSeparator).trim(),
    line.slice(firstSeparator + 3, lastSeparator).trim(),
    line.slice(lastSeparator + 3).trim(),
  ];
  // A legacy Microsoft password may itself contain `---`. Only the valid
  // recovery-email suffix makes the three-part Google shape unambiguous.
  if (!BATCH_EMAIL_PATTERN.test(fields[0]) || !BATCH_EMAIL_PATTERN.test(fields[2])) {
    return null;
  }
  return fields;
}

function splitGoogleBatchAccountLine(line: string, rowNumber: number): ParsedBatchAccount | null {
  const fields = googleCredentialFields(line);
  if (!fields) return null;
  const [email, password] = fields;
  if (!password || password.length > 1024 || /[\x00-\x1f\x7f]/.test(password)) {
    throw new Error(`第 ${rowNumber} 行 Google 密码格式不正确`);
  }
  return { email, password };
}

function compactPipeCredentialFields(line: string): [string, string, string, string] | null {
  const clientSeparator = line.lastIndexOf("|");
  if (clientSeparator <= 0) return null;
  const beforeClient = line.slice(0, clientSeparator);
  const refreshSeparator = beforeClient.lastIndexOf("|");
  if (refreshSeparator <= 0) return null;
  const identityAndPassword = beforeClient.slice(0, refreshSeparator);
  const passwordSeparator = identityAndPassword.indexOf("|");
  if (passwordSeparator <= 0) return null;
  const fields = [
    identityAndPassword.slice(0, passwordSeparator).trim(),
    identityAndPassword.slice(passwordSeparator + 1).trim(),
    beforeClient.slice(refreshSeparator + 1).trim(),
    line.slice(clientSeparator + 1).trim(),
  ];
  if (
    !BATCH_EMAIL_PATTERN.test(fields[0])
    || !OAUTH_CLIENT_ID_PATTERN.test(fields[3])
  ) return null;
  return fields as [string, string, string, string];
}

function splitCompactPipeBatchAccountLine(line: string, rowNumber: number): ParsedBatchAccount | null {
  const fields = compactPipeCredentialFields(line);
  if (!fields) return null;
  const [email, password, rawRefreshToken] = fields;
  if (!password || password.length > 1024 || /[\x00-\x1f\x7f]/.test(password)) {
    throw new Error(`第 ${rowNumber} 行密码格式不正确`);
  }
  validateAuxiliaryRefreshToken(rawRefreshToken, rowNumber);
  return { email, password };
}

function splitTabBatchAccountLine(line: string, rowNumber: number): ParsedBatchAccount {
  const fields = line.split("\t").map((field) => field.trim());
  while (fields.length && !fields[fields.length - 1]) fields.pop();
  if (fields.length === 2) return { email: fields[0], password: fields[1] };
  if (fields.length !== 3 && fields.length !== 4) throw new Error(`第 ${rowNumber} 行制表符凭据格式不正确`);

  const [email, sitePassword, microsoftBundle] = fields;
  const bundleFields = microsoftBundle.split("|");
  let oauthClientId = fields[3] || "";
  if (fields.length === 3 && bundleFields.length === 4) {
    oauthClientId = bundleFields.pop() || "";
  }
  if (bundleFields.length !== 3 || !oauthClientId || !OAUTH_CLIENT_ID_PATTERN.test(oauthClientId)) {
    throw new Error(`第 ${rowNumber} 行 Microsoft 凭据包格式不正确`);
  }
  const [microsoftEmail, microsoftPassword, refreshToken] = bundleFields.map((field) => field.trim());
  if (email.trim().toLowerCase() !== microsoftEmail.toLowerCase()) throw new Error(`第 ${rowNumber} 行邮箱不一致`);
  if (!sitePassword || sitePassword.length > 1024 || !microsoftPassword || microsoftPassword.length > 1024) throw new Error(`第 ${rowNumber} 行密码格式不正确`);
  validateAuxiliaryRefreshToken(refreshToken, rowNumber);
  return { email: email.trim(), password: sitePassword };
}

function validateAuxiliaryRefreshToken(value: string, rowNumber: number): void {
  const normalized = value.trim().replace(/\\\*/g, "*");
  if (
    normalized.length < 8
    || normalized.length > 16_384
    || /\s/.test(normalized)
    || /[\x00-\x1f\x7f]/.test(normalized)
  ) throw new Error(`第 ${rowNumber} 行辅助刷新凭据格式不正确`);
}
